/**
 * 20260903 - TEXT-FIELD AUTO-FOCUS POLICY: the live flag, and the one thing a dialog asks.
 * 20260923 - and the board's tile action bar, which asks the same host a second question.
 *
 * ITS OWN STORE, not a uiStore slice, and that is load-bearing. uiStore statically imports
 * embeddedBridge (for diagLog), which composes the ENTIRE inbound handler table at module load —
 * so any component that gains a uiStore edge drags every bridge feature module into its own test
 * module registry, and every suite that replaces one of those modules wholesale (SceneSettingsModal's
 * does) then dies at collection on a missing `*Handlers` export. This flag has to be readable from
 * dialogs all over the app, several of which have no other reason to know the bridge exists. A leaf
 * store — zustand plus inputCapabilities.ts, which imports nothing of ours — costs them nothing.
 *
 * Store-backed rather than a bare call to shouldAutoFocusTextField(): a keyboard attach/detach
 * arrives mid-session as the `setInputCapabilities` bridge verb, and a plain function read would
 * leave an already-mounted dialog on the stale answer.
 *
 * THE TWO LIVE VALUES ARE HELD SEPARATELY, and that is the merge contract Unity relies on. Each push
 * names ONE device (TimelineWebViewPanel sends `hardwareKeyboard` from the GCKeyboard watcher and
 * `pointerDevice` from the GCMouse one), so a keyboard push must not reset the pointer answer to
 * whatever the URL said at page load, or a trackpad detached ten minutes ago would come back the
 * next time a keyboard moved. `undefined` means "no push yet for this device": the URL seed stands.
 */

import { create } from 'zustand';
import {
  getHostInput,
  shouldAutoFocusTextField,
  boardHoverTakeoverApplies,
  type HostInput,
} from '@/platform/inputCapabilities.ts';

/** The pushed half — what Unity has told us SINCE the page loaded. Absent = never pushed. */
interface LiveInput {
  hardwareKeyboard?: boolean;
  pointerDevice?: boolean;
}

interface InputCapabilitiesState extends LiveInput {
  /** May a dialog's text field take focus on mount? Seeded from the URL at store creation. */
  autoFocusTextFields: boolean;
  /** 20260923 - May the board's selected tile wait for a mouse hover to show its action bar?
   *  False on a bare iPad, where nothing can hover — the bar shows on selection instead. */
  boardHoverTakeover: boolean;
  /** The one half that can change mid-session; platform and form factor stay as the URL had them. */
  setHardwareKeyboardAttached: (attached: boolean) => void;
  /** 20260923 - its twin, for the trackpad/mouse. Independent of the keyboard setter by design. */
  setPointerDeviceAttached: (attached: boolean) => void;
}

/** The URL seed with the pushed values layered over it — only the keys actually pushed, so an
 *  un-pushed device leaves the seed alone instead of overwriting it with `undefined`. */
function resolved(live: LiveInput): HostInput {
  const out: HostInput = { ...getHostInput() };
  if (live.hardwareKeyboard !== undefined) out.hardwareKeyboard = live.hardwareKeyboard;
  if (live.pointerDevice !== undefined) out.pointerDevice = live.pointerDevice;
  return out;
}

// Re-runs the WHOLE policy on every change rather than storing each flag's own verdict: platform and
// form factor come from the URL and never change, so one recompute keeps one decision in one place —
// and both policies read both halves of the host, so neither can be recomputed in isolation.
function recompute(live: LiveInput): Pick<InputCapabilitiesState, 'autoFocusTextFields' | 'boardHoverTakeover'> {
  const input = resolved(live);
  return {
    autoFocusTextFields: shouldAutoFocusTextField(input),
    boardHoverTakeover: boardHoverTakeoverApplies(input, live),
  };
}

export const useInputCapabilitiesStore = create<InputCapabilitiesState>((set, get) => ({
  ...recompute({}),
  setHardwareKeyboardAttached: (attached) => {
    const live: LiveInput = { hardwareKeyboard: attached, pointerDevice: get().pointerDevice };
    set({ ...live, ...recompute(live) });
  },
  setPointerDeviceAttached: (attached) => {
    const live: LiveInput = { hardwareKeyboard: get().hardwareKeyboard, pointerDevice: attached };
    set({ ...live, ...recompute(live) });
  },
}));

export function useShouldAutoFocus(): boolean {
  return useInputCapabilitiesStore((s) => s.autoFocusTextFields);
}

/** 20260923 - the board reads this so a hot-plugged trackpad re-renders the tiles. */
export function useBoardHoverTakeover(): boolean {
  return useInputCapabilitiesStore((s) => s.boardHoverTakeover);
}
